import { addDays } from 'date-fns';
import { format } from 'date-fns';
import type { Matcher } from 'react-day-picker';

import {
  CLEAR_BUTTON_HOVER_GROUP_CLASS,
  CLEAR_BUTTON_ON_HOVER_CLASS,
} from '~/chords/lib/clearButtonOnHover';
import { cn } from '~/chords/lib/cn';
import { variants } from '~/chords/lib/variants';

export type DatePickerVariants = 'default' | 'warning' | 'error';
export type DatePickerAppearance = 'dropdown' | 'input';

export const TODAY = new Date();
TODAY.setHours(0, 0, 0, 0);

export const FUTURE_LIMIT = addDays(TODAY, 365 * 10); // 10 years

export const getDisabledDates = (
  minDate: Date | undefined,
  allowFutureDates: boolean,
) => {
  const matchers: Matcher[] = [];

  if (minDate) {
    matchers.push({ before: minDate });
  }

  if (!allowFutureDates) {
    matchers.push({ after: TODAY });
  }

  return matchers;
};

export const datePickerVariants = variants({
  slots: {
    field: cn(
      CLEAR_BUTTON_HOVER_GROUP_CLASS,
      'relative isolate flex items-center gap-sm py-sm ps-md',
      'rounded-xs outline outline-1 transition-colors-with-outline duration-100 outline-emphasis-subtle',
      'bg-emphasis-default text-default has-[[data-date-picker-trigger]:hover]:bg-emphasis-strong',
    ),
    trigger: cn(
      'flex items-center text-start after:absolute after:inset-0 after:rounded-xs',
      'focus-visible:outline-none focus-visible:after:[outline-style:auto]',
    ),
    text: cn('text-body-sm leading-6'),
    clearButton: cn(
      'relative z-10 flex size-lg shrink-0 items-center justify-center',
      'rounded-full text-default hover:bg-emphasis-strong',
      CLEAR_BUTTON_ON_HOVER_CLASS,
    ),
    trailingIcon: cn('pointer-events-none flex'),
  },
  variants: {
    variant: {
      default: {},
      warning: { field: cn('outline-warning-default') },
      error: { field: cn('outline-error-default') },
    },
    appearance: {
      dropdown: {
        field: cn('pe-sm'),
      },
      input: {
        field: cn('pe-md'),
        trigger: cn('flex-1'),
      },
    },
    isPlaceholder: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      appearance: 'input',
      isPlaceholder: true,
      class: { text: cn('text-subtle') },
    },
  ],
});

export function formatDate(date: Date | undefined) {
  return date ? format(date, 'MMM d, yyyy') : undefined;
}

export function formatTime(date: Date | undefined) {
  return date ? format(date, 'h:mm a') : undefined;
}
